import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import DataTable from '../../components/Common/DataTable';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import { 
  PlusIcon, PencilIcon, TrashIcon, EyeIcon,
  MagnifyingGlassIcon, FunnelIcon, XMarkIcon, CalendarIcon,
  DocumentIcon
} from '@heroicons/react/24/outline';

const Events = () => {
  const [events, setEvents] = useState([]);
  const [pageInfo, setPageInfo] = useState({
    totalElements: 0, totalPages: 0, currentPage: 0, size: 10
  });
  
  const [trips, setTrips] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  
  const [filters, setFilters] = useState({
    uuid: '',
    tripId: '',
    startedFrom: ''
  });
  
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  const { loading, get, del } = useApi();
  const navigate = useNavigate();

  useEffect(() => { fetchDropdownData(); }, []);
  useEffect(() => { fetchEvents(); }, [filters, pageInfo.currentPage, searchTerm]);

  const fetchDropdownData = async () => {
    const tripsRes = await get('/api/trips/filter?size=100');
    if (tripsRes.data) setTrips(tripsRes.data.content || []);
    const eventTypesRes = await get('/api/event-types');
    if (eventTypesRes.data) setEventTypes(eventTypesRes.data);
  };

  const fetchEvents = async () => {
    const params = new URLSearchParams();
    params.append('page', pageInfo.currentPage);
    params.append('size', pageInfo.size);
    
    if (filters.uuid) params.append('uuid', filters.uuid);
    if (filters.tripId) params.append('tripId', filters.tripId);
    if (filters.startedFrom) params.append('startedFrom', filters.startedFrom);
    
    if (searchTerm && !filters.uuid) params.append('uuid', searchTerm);
    
    const endpoint = `/api/events/filter?${params.toString()}`;
    const { data } = await get(endpoint);
    if (data) {
      setEvents(data.content || []);
      setPageInfo({
        totalElements: data.totalElements, totalPages: data.totalPages,
        currentPage: data.number || 0, size: data.size || 10
      });
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this event?')) {
      const { error } = await del(`/api/events/${id}`);
      if (!error) fetchEvents();
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setPageInfo(prev => ({ ...prev, currentPage: 0 }));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchEvents();
  };

  const clearFilters = () => {
    setFilters({ uuid: '', tripId: '', startedFrom: '' });
    setSearchTerm('');
    setPageInfo(prev => ({ ...prev, currentPage: 0 }));
  };

  const columns = [
    { header: 'ID', accessor: 'id' },
    { header: 'UUID', accessor: (row) => row.evtUuid ? `${row.evtUuid.substring(0, 8)}...` : '-' },
    { header: 'Trip ID', accessor: 'tripId' },
    { header: 'Event Type', accessor: (row) => row.eventType?.description || '-' },
    // CHANGED: File column - now shows count of files instead of single file ID
    { 
      header: 'Files', 
      accessor: (row) => {
        const fileCount = row.fileIds?.length || 0;
        return (
          <div className="flex items-center space-x-1">
            <DocumentIcon className="h-4 w-4 text-gray-400" />
            <span className="text-sm">{fileCount}</span>
          </div>
        );
      }
    },
    { header: 'Reference', accessor: (row) => row.referenceNum || '-' },
    { header: 'Timestamp', accessor: (row) => row.timestamp ? new Date(row.timestamp).toLocaleString() : '-' },
    { 
      header: 'Actions', 
      accessor: (row) => (
        <div className="flex space-x-2">
          <button
            onClick={() => navigate(`/events/${row.id}`)}
            className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
            title="View"
          >
            <EyeIcon className="h-5 w-5" />
          </button>
          <button
            onClick={() => navigate(`/events/edit/${row.id}`)}
            className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300"
            title="Edit"
          >
            <PencilIcon className="h-5 w-5" />
          </button>
          <button
            onClick={() => handleDelete(row.id)}
            className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
            title="Delete"
          >
            <TrashIcon className="h-5 w-5" />
          </button>
        </div>
      )
    }
  ];

  if (loading) return <LoadingSpinner />;

  return (
    <div className="p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Events</h1>
        <button
          onClick={() => navigate('/events/add')}
          className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 flex items-center space-x-2 w-full sm:w-auto justify-center"
        >
          <PlusIcon className="h-5 w-5" />
          <span>Create Event</span>
        </button>
      </div>

      <div className="mb-4">
        <form onSubmit={handleSearch} className="flex gap-2">
          <div className="flex-1 relative">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by UUID..."
              className="input-field pl-10"
            />
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          </div>
          <button
            type="submit"
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            Search
          </button>
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            className={`px-4 py-2 border rounded-lg flex items-center space-x-2 ${
              showFilters ? 'bg-indigo-50 border-indigo-300 text-indigo-700 dark:bg-indigo-900 dark:border-indigo-700 dark:text-indigo-300' : 'border-gray-300 text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
            }`}
          >
            <FunnelIcon className="h-5 w-5" />
            <span className="hidden sm:inline">Filters</span>
          </button>
          {(filters.uuid || filters.tripId || filters.startedFrom || searchTerm) && (
            <button
              type="button"
              onClick={clearFilters}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 flex items-center space-x-2"
            >
              <XMarkIcon className="h-5 w-5" />
              <span className="hidden sm:inline">Clear</span>
            </button>
          )}
        </form>
      </div>

      {showFilters && (
        <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center">
            <FunnelIcon className="h-4 w-4 mr-1" /> Filters
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                UUID
              </label>
              <input
                type="text"
                name="uuid"
                value={filters.uuid}
                onChange={handleFilterChange}
                placeholder="Filter by UUID"
                className="input-field text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                Trip
              </label>
              <select
                name="tripId"
                value={filters.tripId}
                onChange={handleFilterChange}
                className="input-field text-sm"
              >
                <option value="">All Trips</option>
                {trips.map(trip => (
                  <option key={trip.id} value={trip.id}>
                    Trip #{trip.id} - {trip.label || 'No label'}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 flex items-center">
                <CalendarIcon className="h-3 w-3 mr-1" /> Started From
              </label>
              <input
                type="datetime-local"
                name="startedFrom"
                value={filters.startedFrom || ''}
                onChange={handleFilterChange}
                className="input-field text-sm"
              />
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <span className="text-xs text-gray-500 dark:text-gray-400 mr-2 self-center">Quick dates:</span>
            <button
              onClick={() => {
                const yesterday = new Date(); yesterday.setDate(yesterday.getDate() - 1);
                setFilters(prev => ({ ...prev, startedFrom: yesterday.toISOString().slice(0, 16) }));
              }}
              className="px-3 py-1 text-xs bg-purple-100 text-purple-800 rounded-full hover:bg-purple-200 dark:bg-purple-900 dark:text-purple-200 dark:hover:bg-purple-800"
            >
              Last 24 Hours
            </button>
            <button
              onClick={() => {
                const weekAgo = new Date(); weekAgo.setDate(weekAgo.getDate() - 7);
                setFilters(prev => ({ ...prev, startedFrom: weekAgo.toISOString().slice(0, 16) }));
              }}
              className="px-3 py-1 text-xs bg-purple-100 text-purple-800 rounded-full hover:bg-purple-200 dark:bg-purple-900 dark:text-purple-200 dark:hover:bg-purple-800"
            >
              Last 7 Days
            </button>
            <button
              onClick={() => {
                const monthAgo = new Date(); monthAgo.setMonth(monthAgo.getMonth() - 1);
                setFilters(prev => ({ ...prev, startedFrom: monthAgo.toISOString().slice(0, 16) }));
              }}
              className="px-3 py-1 text-xs bg-purple-100 text-purple-800 rounded-full hover:bg-purple-200 dark:bg-purple-900 dark:text-purple-200 dark:hover:bg-purple-800"
            >
              Last 30 Days
            </button>
          </div>
        </div>
      )}

      <div className="mb-4 text-sm text-gray-600 dark:text-gray-400">
        Showing {events.length} of {pageInfo.totalElements} events
      </div>

      <DataTable 
        columns={columns} 
        data={events}
        pageInfo={pageInfo}
        onPageChange={(page) => setPageInfo({...pageInfo, currentPage: page})}
      />
    </div>
  );
};

export default Events;